import React, { useEffect, useState } from 'react';
import { Paper, Box, Grid2 } from '@mui/material';
import { Typography, Stack, Avatar } from '@mui/material';
import palette from '../assets/sass/palette.scss';
import { useGameUtils } from '../utilities/GameUtils';
import dayjs from 'dayjs';
import PlayerStatPill from './PlayerStatPill';
// eslint-disable-next-line no-unused-vars
import LockIcon from '@mui/icons-material/Lock';
import GameTeeTime from './GameTeeTime';
import { Timestamp } from 'firebase/firestore';

const MatchViewOnly = ({ gameData, matchNumber }) => {
  const { createCombinedInvitationsPlayers, getAllUserDetailsByUserIds } = useGameUtils();
  const [allUsers, setAllUsers] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState();
  const [amountOfPlayers, setAmountOfPlayers] = useState(0);
  const [teaTime, setTeaTime] = useState();
  const [slotPlayers, setSlotPlayers] = useState([null, null, null, null]);

  useEffect(() => {
    const combinedInvitationsPlayers = createCombinedInvitationsPlayers(gameData);
    const getAllUsers = async () => {
      const users = await getAllUserDetailsByUserIds(
        combinedInvitationsPlayers.map((user) => user.user)
      );
      setAllUsers(users);

      // Initialize slotPlayers from gameData.matches if it exists
      if (gameData.matches && gameData.matches[matchNumber - 1]) {
        const match = gameData.matches[matchNumber - 1];
        if (match.players && match.players.length > 0) {
          const initialSlotPlayers = Array(4).fill(null);
          match.players.forEach((player, index) => {
            // Find the full user data from allUsers
            const fullUserData = users.find((u) => u.user === player.uid);
            if (fullUserData) {
              initialSlotPlayers[index] = {
                ...fullUserData,
                group: player.group,
                handicap: fullUserData.handicap || 0,
              };
            } else {
              // Fallback for when user data is not available yet
              // Create a basic player object from the match data
              initialSlotPlayers[index] = {
                user: player.uid,
                uid: player.uid,
                userName: player.userName || `Player ${index + 1}`,
                initial: (player.userName || `P${index + 1}`).charAt(0).toUpperCase(),
                photoURL: player.photoURL || null,
                group: player.group,
                handicap: player.handicap || 0,
                waglRating: player.waglRating || 50,
              };
            }
          });
          setSlotPlayers(initialSlotPlayers);

          // Set amountOfPlayers based on the number of players in the match
          if (match.format === 'match') {
            setAmountOfPlayers(match.players.length === 2 ? '1v1' : '2v2');
          } else {
            setAmountOfPlayers(match.players.length);
          }
        }
        if (match.teaTime) {
          setTeaTime(dayjs(match.teaTime.toDate()));
        }
      }
    };
    getAllUsers();

    // Set the format based on gameData or match data
    const matchFormat = gameData.matches?.[matchNumber - 1]?.format;
    const defaultFormat = matchFormat || gameData.format || 'match';
    setSelectedFormat(defaultFormat);

    // Only set default amountOfPlayers if there are no existing players
    const existingMatch = gameData.matches?.[matchNumber - 1];
    if (!existingMatch?.players?.length) {
      if (defaultFormat === 'match') {
        setAmountOfPlayers('1v1'); // Default to 1v1 for match play
      } else {
        setAmountOfPlayers(2); // Default to 2 for stroke play
      }
    }

    setTeaTime(dayjs());
  }, [gameData, matchNumber, createCombinedInvitationsPlayers, getAllUserDetailsByUserIds]);

  // Update slot players when allUsers becomes available
  useEffect(() => {
    if (allUsers.length > 0 && gameData.matches && gameData.matches[matchNumber - 1]) {
      const match = gameData.matches[matchNumber - 1];
      if (match.players && match.players.length > 0) {
        const updatedSlotPlayers = [...slotPlayers];
        let hasUpdates = false;

        match.players.forEach((player, index) => {
          const fullUserData = allUsers.find((u) => u.user === player.uid);
          if (fullUserData && (!updatedSlotPlayers[index] || !updatedSlotPlayers[index].userName)) {
            // Update with full user data if we have it and the slot doesn't have complete data
            updatedSlotPlayers[index] = {
              ...fullUserData,
              group: player.group,
              handicap: fullUserData.handicap || 0,
            };
            hasUpdates = true;
          }
        });

        if (hasUpdates) {
          setSlotPlayers(updatedSlotPlayers);
        }
      }
    }
  }, [allUsers, gameData.matches, matchNumber, slotPlayers]); // Added slotPlayers to dependency array

  // Helper to get slot count based on format and selection
  const getSlotCount = () => {
    if (selectedFormat === 'match') {
      if (amountOfPlayers === '1v1') return 2;
      if (amountOfPlayers === '2v2') return 4;
      return 2;
    } else {
      return Number(amountOfPlayers) || 2;
    }
  };
  const slotCount = getSlotCount();

  const VsDot = () => {
    return (
      <Grid2 size={{ xs: 12, md: 2 }} sx={{ textAlign: 'center', flexGrow: 1 }}>
        <Box
          sx={{
            backgroundColor: palette.darkGreen,
            color: 'white',
            borderRadius: '50%',
            width: '48px',
            height: '48px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '48px auto',
          }}
        >
          <Typography variant="strong" sx={{ fontWeight: 700 }}>
            VS
          </Typography>
        </Box>
      </Grid2>
    );
  };

  const SelectedPlayer = ({ idx, player, children = null }) => {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Box
          sx={{
            borderRadius: '50%',
            width: 80,
            height: 80,
            mx: 'auto',
            mb: 1,
          }}
        >
          {player.photoURL ? (
            <Avatar
              alt={player.userName || player.userId}
              src={player.photoURL}
              sx={{ width: 80, height: 80, boxShadow: 2 }}
            />
          ) : (
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: idx % 2 ? palette.paleGreen : palette.palePurple,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="factorA"
                sx={{ fontSize: '24px', color: '#4C4C4C', fontWeight: 700 }}
              >
                {player.initial}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography
          sx={{
            fontFamily: '"FactorA Medium", arial, sans-serif',
            fontWeight: 700,
            fontSize: '16px',
            textAlign: 'center',
            mb: 0.5,
          }}
        >
          {player.userName}
        </Typography>
        <PlayerStatPill
          waglScore={player?.waglRating?.toFixed(0) || '50'}
          handicap={player.handicap || 0}
        />
        {children ?? children}
      </Box>
    );
  };

  const EmptySlot = () => {
    return (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Box
          sx={{
            borderRadius: '50%',
            width: 80,
            height: 80,
            mx: 'auto',
            mb: 1,
            bgcolor: palette.borderGrey,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="factorA" sx={{ fontSize: '24px', color: '#999', fontWeight: 700 }}>
            -
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: '"FactorA Medium", arial, sans-serif',
            fontWeight: 700,
            fontSize: '16px',
            textAlign: 'center',
            mb: 0.5,
            color: '#999',
          }}
        >
          Empty Slot
        </Typography>
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      variant="gameBox"
      sx={{
        border: '2px solid black',
        position: 'relative',
        cursor: 'default',
        mb: 0,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ justifyContent: 'space-between', width: '100%' }}
        spacing={2}
      >
        <Typography variant="moduleHeader" component="h5">
          Match #{matchNumber}
        </Typography>
        <GameTeeTime teeTime={teaTime} Timestamp={Timestamp} />
      </Stack>
      <Grid2
        container
        spacing={0}
        sx={{ mt: 2, mb: 2 }}
        columns={{ xs: 12, md: selectedFormat === 'match' ? 10 : 12 }}
      >
        {selectedFormat === 'match'
          ? Array.from({ length: slotCount }).map((_, idx) => {
              const player = slotPlayers[idx];
              return (
                <React.Fragment key={idx}>
                  <Grid2
                    sx={{ flexGrow: 1, pt: 2 }}
                    size={{ xs: 6, md: slotCount === 2 ? 4 : slotCount > 2 ? 2 : 6 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        alignItems: 'center',
                        width: '100%',
                        borderRight:
                          (idx < 1 || idx > 1) && idx + 1 < slotCount && slotCount !== 2
                            ? `1px solid ${palette.borderGrey}`
                            : 'none',
                      }}
                    >
                      {player ? (
                        <SelectedPlayer idx={idx} player={player}>
                          {/* Group badge for 2v2 only */}
                          {slotCount === 4 && (
                            <Box sx={{ mt: 1 }}>
                              {/* <Chip
                                label={`Group ${group}`}
                                size="small"
                                color={group === 'A' ? 'primary' : 'success'}
                              /> */}
                            </Box>
                          )}
                        </SelectedPlayer>
                      ) : (
                        <EmptySlot />
                      )}
                    </Box>
                  </Grid2>
                  {/* Insert VS in the middle for match play */}
                  {slotCount === 2 && idx === 0 && <VsDot slotCount={slotCount} />}
                  {slotCount === 4 && idx === 1 && <VsDot slotCount={slotCount} />}
                </React.Fragment>
              );
            })
          : Array.from({ length: slotCount }).map((_, idx) => {
              // For stroke play (2-4 slots)
              const player = slotPlayers[idx];
              return (
                <React.Fragment key={idx}>
                  <Grid2 size={{ xs: 6, md: slotCount === 4 ? 3 : slotCount === 3 ? 4 : 6 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        alignItems: 'center',
                        width: '100%',
                        my: 2,
                        borderRight: {
                          xs: idx < 1 || idx === 2 ? `1px solid ${palette.borderGrey}` : 'none',
                          md: idx + 1 < slotCount ? `1px solid ${palette.borderGrey}` : 'none',
                        },
                      }}
                    >
                      {player ? <SelectedPlayer idx={idx} player={player} /> : <EmptySlot />}
                    </Box>
                  </Grid2>
                </React.Fragment>
              );
            })}
      </Grid2>
    </Paper>
  );
};

export default MatchViewOnly;
